import { Container, Graphics, NineSliceSprite, Sprite, Texture } from "pixi.js";

import type { IOverlayElementImage } from "../../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Overlay.interface";

interface IImageRenderArgs {
  element: IOverlayElementImage;
  destination: { width: number; height: number };
}

/**
 * Pixi pipeline equivalent of the DOM `OverlayElementImage`. Same contract as
 * `OverlayShapeRenderer` / `OverlayTextRenderer`: synchronous `render()` that
 * positions a wrapper container immediately, then asynchronously fills it with
 * the loaded sprite. While the texture is loading the slot stays empty —
 * matches the natural `<img>` behaviour the streamer already sees in the DOM
 * preview.
 *
 * Texture loading uses `new Image()` → `Texture.from(img)` (mirroring
 * `BackgroundImageRenderer`) so the browser's native image decoder handles
 * `data:image/svg+xml;base64,...` URLs without depending on Pixi's `loadSvg`
 * parser, which keys off URL extension and skips data URLs by default.
 *
 * Supported `fit` modes: `cover` (with crop mask), `contain`, `fill`, y
 * `9-slice` (vía `NineSliceSprite`, con la misma convención de `sliceRect` que
 * el DOM: left/top = inset; right/bottom = texDim − (left+width / top+height)).
 * Sin `sliceRect`, `9-slice` cae a `fill` estirado.
 */
export class OverlayImageRenderer {
  /**
   * Per-`src` texture cache. Multiple intermission scenes share the same
   * decoration data-URL via copy-on-apply, and re-renders during edit hit
   * the same source — decoding once spares the GPU on every refresh.
   */
  private textureCache = new Map<string, Promise<Texture>>();

  render(container: Container, args: IImageRenderArgs): void {
    const { element, destination } = args;
    const { src, fit, sliceRect } = element.options;

    const wrapper = new Container();

    if (element.rotation) {
      const rad = (element.rotation * Math.PI) / 180;
      const pivotX = destination.width / 2;
      const pivotY = destination.height / 2;
      wrapper.pivot.set(pivotX, pivotY);
      wrapper.position.set(
        element.bounds.left + pivotX,
        element.bounds.top + pivotY,
      );
      wrapper.rotation = rad;
    } else {
      wrapper.position.set(element.bounds.left, element.bounds.top);
    }

    container.addChild(wrapper);

    if (!src) return;

    void this.loadTexture(src)
      .then((texture) => {
        if (wrapper.destroyed) return;
        const node = this.buildFittedNode(
          texture,
          destination.width,
          destination.height,
          fit,
          sliceRect,
        );
        if (node) wrapper.addChild(node);
      })
      .catch(() => {
        // Leave the slot empty on load failure. The OverlayInspector flags
        // missing image refs separately so the streamer notices.
      });
  }

  private loadTexture(src: string): Promise<Texture> {
    const cached = this.textureCache.get(src);
    if (cached) return cached;

    const pending = new Promise<Texture>((resolve, reject) => {
      const img = new Image();
      // crossOrigin matters for HTTP URLs that need to be sampled by WebGL
      // without tainting the canvas. Harmless on data URLs.
      img.crossOrigin = "anonymous";
      img.onload = () => {
        try {
          const texture = Texture.from(img);
          resolve(texture);
        } catch (err) {
          reject(err);
        }
      };
      img.onerror = (err) =>
        reject(err instanceof Error ? err : new Error("image load failed"));
      img.src = src;
    });

    this.textureCache.set(src, pending);
    pending.catch(() => this.textureCache.delete(src));
    return pending;
  }

  private buildFittedNode(
    texture: Texture,
    destW: number,
    destH: number,
    fit: IOverlayElementImage["options"]["fit"],
    sliceRect?: IOverlayElementImage["options"]["sliceRect"],
  ): Container | null {
    if (destW <= 0 || destH <= 0) return null;

    const texW = texture.width || destW;
    const texH = texture.height || destH;

    // 9-slice real: corners fijos, bordes/centro estirados. Misma convención de
    // sliceRect que el DOM (left/top = inset; right/bottom = texDim − (l+w/t+h)).
    if (fit === "9-slice" && sliceRect) {
      const leftWidth = Math.max(0, Math.round(sliceRect.left));
      const topHeight = Math.max(0, Math.round(sliceRect.top));
      const rightWidth = Math.max(
        0,
        Math.round(texW - (sliceRect.left + sliceRect.width)),
      );
      const bottomHeight = Math.max(
        0,
        Math.round(texH - (sliceRect.top + sliceRect.height)),
      );
      const nine = new NineSliceSprite({
        texture,
        leftWidth,
        topHeight,
        rightWidth,
        bottomHeight,
      });
      nine.width = destW;
      nine.height = destH;
      return nine as unknown as Container;
    }

    const sprite = new Sprite(texture);

    if (fit === "fill" || fit === "9-slice") {
      // 9-slice sin sliceRect cae a fill estirado (evita slot vacío).
      sprite.width = destW;
      sprite.height = destH;
      return sprite;
    }

    const scale =
      fit === "cover"
        ? Math.max(destW / texW, destH / texH)
        : Math.min(destW / texW, destH / texH);
    const renderedW = texW * scale;
    const renderedH = texH * scale;

    sprite.width = renderedW;
    sprite.height = renderedH;
    sprite.x = (destW - renderedW) / 2;
    sprite.y = (destH - renderedH) / 2;

    if (fit === "cover") {
      // Crop overflow with a rect mask sized to the destination so the
      // sprite's overflowing pixels don't bleed onto sibling elements.
      const out = new Container();
      out.addChild(sprite);
      const mask = new Graphics();
      mask.rect(0, 0, destW, destH).fill(0xffffff);
      out.addChild(mask);
      out.mask = mask;
      return out;
    }

    return sprite;
  }
}

export default OverlayImageRenderer;
